import {ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {AsyncPipe} from "@angular/common";
import {BehaviorSubject, distinctUntilChanged, from, merge, of, Subscription} from "rxjs";
import {AbstractControl, FormGroupDirective, ValidationErrors} from "@angular/forms";
import {FORM_ERRORS} from "@shared/components/forms/app-control-error/messages/app-control-error-messages.config";
import {TranslateModule, TranslateService} from "@ngx-translate/core";

// https://medium.com/@iamjustin/simplifying-form-error-validations-in-angular-f21adea5d028
@Component({
    standalone: true,
    selector: 'app-control-error',
    imports: [AsyncPipe, TranslateModule],
    template: '<small class="text-danger">{{ message$ | async }}</small>',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppControlErrorComponent implements OnInit, OnDestroy {
    private subscription = new Subscription();
    private formGroupDirective = inject(FormGroupDirective, {optional: true});
    errors = inject(FORM_ERRORS);
    message$ = new BehaviorSubject<string>('');
    translateService = inject(TranslateService);

    @Input() controlName = '';
    @Input() control?: AbstractControl | null;
    @Input() customErrors?: ValidationErrors;

    ngOnInit(): void {
        const control = this.getControl();
        const ngSubmit$ = this.formGroupDirective?.ngSubmit ? from(this.formGroupDirective.ngSubmit) : of(null);

        if (control) {
            this.subscription = merge(control.statusChanges, control.valueChanges, ngSubmit$)
                .pipe(distinctUntilChanged())
                .subscribe(() => {
                    setTimeout(() => {
                        const controlErrors = control.errors;
                        if (controlErrors) {
                            const firstKey = Object.keys(controlErrors)[0];
                            const getError = this.errors[firstKey];
                            const text = this.translateService.instant(
                                this.customErrors?.[firstKey] || getError(controlErrors[firstKey]),
                                controlErrors[firstKey]
                            );
                            this.setError(text);
                        } else {
                            this.setError('');
                        }
                    }, 100);
                });
        } else {
            const message = this.controlName
                ? `Control "${this.controlName}" not found in the form group.`
                : `Input control or controlName is required`;
            console.error(message);
        }
    }

    private getControl(): AbstractControl | null {
        if (this.control != null) {
            return this.control;
        }

        if (this.formGroupDirective && this.controlName) {
            return this.formGroupDirective.control.get(this.controlName); // Reativo
        }

        return null;
    }

    setError(text: string) {
        this.message$.next(text);
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
